// Public form definitions only. Share in-flight work, never cache patient data
// or completed configuration; settings refreshes must reach the API.
const pending = new Map();

const read = async (url, headers) => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 30000);
  try {
    const response = await fetch(url, { headers, cache: 'no-store', signal: controller.signal });
    return { ok: response.ok, status: response.status, data: response.ok ? await response.json() : null };
  } catch (error) {
    if (error.name === 'AbortError') throw new Error('Loading form timed out. Please try again.');
    throw error;
  } finally { clearTimeout(timeout); }
};

const requireSuccess = (result, label) => {
  if (!result.ok) throw new Error(`Failed to load ${label}: ${result.status}`);
  return result.data;
};

const validate = data => {
  if (!Array.isArray(data?.modules)) throw new Error('Invalid modules format received from server');
  if (!data.fieldDefinitions || data.modules.some(module =>
    !Object.prototype.hasOwnProperty.call(data.fieldDefinitions, module.module_name))) {
    throw new Error('Incomplete form definitions received from server');
  }
  return data;
};

export const loadIntakeDefinition = ({ baseUrl, tenantId, tenantSlug }) => {
  const headers = tenantId ? { 'X-Tenant-ID': String(tenantId) } : {};
  const query = !tenantId && tenantSlug ? `?tenant=${encodeURIComponent(tenantSlug)}` : '';
  const path = '/api/modules/intakeBootstrap';
  // Only a server advertising the narrowly scoped route opts into same-origin.
  // Static/older deployments retain the existing direct API request.
  const bootstrapBase = window.__INTAKE_TENANT_HINT__?.bootstrapPath === path ? '' : baseUrl;
  const url = `${bootstrapBase}${path}?images=display-v1${query ? `&${query.slice(1)}` : ''}`;
  const key = `${url}|${tenantId || ''}`;
  if (pending.has(key)) return pending.get(key);

  const early = window.__INTAKE_REQUEST__;
  const matches = early?.url === url && early.tenantId === String(tenantId || '');
  const request = (async () => {
    let result = matches ? await early.promise.catch(() => null) : null;
    if (!result) result = await read(url, headers);

    // Additive rollout: old APIs keep their existing module + field contract.
    if (result.status === 404 || result.status === 405) {
      const modules = requireSuccess(await read(`${baseUrl}/api/modules/getAllModules${query}`, headers), 'form modules');
      if (!Array.isArray(modules?.modules)) throw new Error('Invalid modules format received from server');
      const fieldsTenantId = tenantId || modules.tenantId;
      const fieldHeaders = fieldsTenantId ? { 'X-Tenant-ID': String(fieldsTenantId) } : {};
      const entries = await Promise.all(modules.modules.map(async module => {
        const fields = requireSuccess(await read(
          `${baseUrl}/api/modules/getFields/${encodeURIComponent(module.module_name)}?view=intake`, fieldHeaders
        ), `form fields for ${module.module_name}`);
        return [module.module_name, fields];
      }));
      return validate({ ...modules, fieldDefinitions: Object.fromEntries(entries) });
    }
    return validate(requireSuccess(result, 'form definitions'));
  })().finally(() => {
    if (pending.get(key) === request) pending.delete(key);
    if (matches && window.__INTAKE_REQUEST__ === early) delete window.__INTAKE_REQUEST__;
  });
  pending.set(key, request);
  return request;
};
